import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from "react";
import { Box, CircularProgress, Fab, Fade, Typography, alpha, useTheme } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import { Virtuoso } from "react-virtuoso";
import ClientPortalMessageBubble from "./ClientPortalMessageBubble";
import { isOptimisticPortalMessageId } from "./utils/portalOptimisticMessage";

const INITIAL_FIRST_ITEM_INDEX = 100_000;
const LIST_BOTTOM_SPACER_PX = 20;
const AT_BOTTOM_THRESHOLD_PX = 120;

const ListFooter = () => <div style={{ height: LIST_BOTTOM_SPACER_PX, flexShrink: 0 }} />;

const PortalVirtuosoScroller = forwardRef(function PortalVirtuosoScroller({ style, ...props }, ref) {
  return (
    <div
      ref={ref}
      {...props}
      style={{
        ...style,
        overflowX: "hidden",
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    />
  );
});

function scheduleScrollToBottom(scrollFn, behavior = "auto") {
  let cancelled = false;
  const id1 = requestAnimationFrame(() => {
    if (cancelled) return;
    requestAnimationFrame(() => {
      if (!cancelled) scrollFn(behavior);
    });
  });
  return () => {
    cancelled = true;
    cancelAnimationFrame(id1);
  };
}

const ClientPortalMessageList = forwardRef(function ClientPortalMessageList(
  {
    ticketId,
    messages,
    hasMore,
    isLoading,
    isLoadingMore,
    onLoadMore,
    scrollToBottomNonce = 0,
    onQuickReply,
    canReply = true,
    isSending = false,
  },
  ref
) {
  const theme = useTheme();
  const virtuosoRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const tailSnapshotRef = useRef({ len: 0, lastId: null });
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_FIRST_ITEM_INDEX);
  const snapshotRef = useRef({ firstId: null, len: 0 });

  const listBgSx = useMemo(
    () => ({
      flex: 1,
      minHeight: 0,
      bgcolor: alpha(theme.palette.background.default, 0.45),
    }),
    [theme]
  );

  const scrollToBottom = useCallback((behavior = "auto") => {
    if (!virtuosoRef.current || !messages.length) return;
    virtuosoRef.current.scrollToIndex({
      index: "LAST",
      align: "end",
      behavior,
    });
    isAtBottomRef.current = true;
    setIsAtBottom(true);
  }, [messages.length]);

  useImperativeHandle(
    ref,
    () => ({
      scrollToBottom,
    }),
    [scrollToBottom]
  );

  useEffect(() => {
    setFirstItemIndex(INITIAL_FIRST_ITEM_INDEX);
    setInitialScrollDone(false);
    isAtBottomRef.current = true;
    setIsAtBottom(true);
    tailSnapshotRef.current = { len: 0, lastId: null };
    snapshotRef.current = { firstId: null, len: 0 };
  }, [ticketId]);

  useEffect(() => {
    const firstId = messages[0]?.id ?? null;
    const firstNumeric = Number(firstId);
    const len = messages.length;
    const snap = snapshotRef.current;
    const snapNumeric = Number(snap.firstId);

    if (
      Number.isFinite(firstNumeric) &&
      Number.isFinite(snapNumeric) &&
      firstNumeric < snapNumeric &&
      len > snap.len
    ) {
      setFirstItemIndex((idx) => idx - (len - snap.len));
    }

    snapshotRef.current = { firstId, len };
  }, [messages]);

  useEffect(() => {
    if (!initialScrollDone && messages.length > 0) {
      let cancelled = false;
      const id1 = requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          scrollToBottom("auto");
          setInitialScrollDone(true);
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id1);
      };
    }
    return undefined;
  }, [messages.length, initialScrollDone, ticketId, scrollToBottom]);

  useEffect(() => {
    if (!scrollToBottomNonce) return undefined;
    return scheduleScrollToBottom(scrollToBottom, "auto");
  }, [scrollToBottomNonce, scrollToBottom]);

  useEffect(() => {
    const len = messages.length;
    const lastMsg = len > 0 ? messages[len - 1] : null;
    const lastId = lastMsg?.id ?? null;
    const snap = tailSnapshotRef.current;
    const grewAtEnd = len > snap.len && lastId !== snap.lastId;

    tailSnapshotRef.current = { len, lastId };

    if (!grewAtEnd || !lastMsg) return undefined;

    const isOwnOutgoing =
      isOptimisticPortalMessageId(lastId) || lastMsg.sender === "client";
    const shouldFollow = isOwnOutgoing || isAtBottomRef.current;

    if (!shouldFollow) return undefined;

    return scheduleScrollToBottom(scrollToBottom, isOwnOutgoing ? "auto" : "smooth");
  }, [messages, scrollToBottom]);

  const handleAtBottomStateChange = useCallback((atBottom) => {
    isAtBottomRef.current = atBottom;
    setIsAtBottom(atBottom);
  }, []);

  const renderItem = useCallback(
    (_index, msg) => (
      <ClientPortalMessageBubble
        msg={msg}
        onQuickReply={onQuickReply}
        canReply={canReply}
        isSending={isSending}
      />
    ),
    [onQuickReply, canReply, isSending]
  );

  const ListHeader = useCallback(() => {
    if (!isLoadingMore) return <div style={{ height: 4 }} />;
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }, [isLoadingMore]);

  const virtuosoComponents = useMemo(
    () => ({
      Header: ListHeader,
      Footer: ListFooter,
      Scroller: PortalVirtuosoScroller,
    }),
    [ListHeader]
  );

  if (!messages.length && !isLoading) {
    return (
      <Box
        sx={{
          ...listBgSx,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: 4,
          px: 2,
        }}
      >
        <ForumOutlinedIcon sx={{ fontSize: 44, color: "text.disabled", mb: 1.25, opacity: 0.7 }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
          A conversa começa aqui
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
          Envie sua mensagem no campo abaixo. Nossa equipe ou o assistente virtual responderá por aqui.
        </Typography>
      </Box>
    );
  }

  if (isLoading && !messages.length) {
    return (
      <Box sx={{ ...listBgSx, display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...listBgSx,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Virtuoso
        key={ticketId}
        ref={virtuosoRef}
        data={messages}
        firstItemIndex={firstItemIndex}
        computeItemKey={(_index, msg) => String(msg?.id)}
        initialTopMostItemIndex={Math.max(0, messages.length - 1)}
        alignToBottom
        atBottomThreshold={AT_BOTTOM_THRESHOLD_PX}
        followOutput={(atBottom) => (atBottom ? "auto" : false)}
        increaseViewportBy={{ top: 320, bottom: 640 }}
        defaultItemHeight={80}
        startReached={() => {
          if (hasMore && !isLoadingMore && typeof onLoadMore === "function") {
            onLoadMore();
          }
        }}
        components={virtuosoComponents}
        itemContent={renderItem}
        atBottomStateChange={handleAtBottomStateChange}
        style={{ height: "100%", width: "100%" }}
      />

      <Fade in={!isAtBottom && messages.length > 0}>
        <Fab
          color="primary"
          size="small"
          onClick={() => scrollToBottom("smooth")}
          aria-label="Ir para mensagens recentes"
          sx={{
            position: "absolute",
            bottom: 12,
            right: 12,
            zIndex: 2,
          }}
        >
          <ArrowDownwardIcon />
        </Fab>
      </Fade>
    </Box>
  );
});

export default ClientPortalMessageList;
